/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { PINCode, DialPad, Header } from "../../components";
import { AlertCircle } from "../../assets/svgs";
import { ACCOUNT_STATE, EVENT_NAME, ROUTES } from "../../constants";
import useApp from "../../hooks/useApp";
import useNavigateWithQuery from "../../hooks/useNavigateWithQuery";
import api from "../../services";
import { logAnalyticEvent } from "../../analytics";
import "./styles.scss";
import { convertTimestamp } from "../../utils";

const VerifyPIN = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithQuery();
  const { isRTL, tenantConfig, selectedLanguage } = useApp();
  const location = useLocation();
  const { phoneNumber, accountState, isNotFirstTime } = location.state || {};

  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [isPinValid, setIsPinValid] = useState({ isInvalid: false });
  const [pinState, setPinState] = useState(ACCOUNT_STATE.CREATE);
  const [startTime, setStartTime] = useState(0);

  const handleNavigation = (data) => {
    if (data.auth_strategy_status === "FULL" && data.next_auth_factor == null) {
      if (accountState === ACCOUNT_STATE.CHANGE_PASSWORD) {
        navigate(`/${ROUTES.SETTINGS}`);
      } else {
        navigate(`/${ROUTES.MERCHANT_SUPPORT}`);
      }
    }
  };

  const handleNumberPress = (number) => {
    if (
      (pinState === ACCOUNT_STATE.CREATE ||
        pinState === ACCOUNT_STATE.LOGIN ||
        pinState === ACCOUNT_STATE.FORGOT ||
        pinState === ACCOUNT_STATE.CHANGE_PASSWORD) &&
      pin.length < 4
    ) {
      setPin((prev) => prev + number);
    } else if (pinState === ACCOUNT_STATE.CONFIRM && confirmPin.length < 4) {
      setConfirmPin((prev) => prev + number);
    }
  };

  const handleDelete = () => {
    if (
      pinState === ACCOUNT_STATE.CREATE ||
      pinState === ACCOUNT_STATE.LOGIN ||
      pinState === ACCOUNT_STATE.FORGOT ||
      pinState === ACCOUNT_STATE.CHANGE_PASSWORD
    ) {
      setPin((prev) => prev.slice(0, -1));
    } else if (pinState === ACCOUNT_STATE.CONFIRM) {
      setConfirmPin((prev) => prev.slice(0, -1));
    }
  };

  const handleLogin = async () => {
    try {
      const response = await api.loginSetPin(
        isNotFirstTime ? { pin, username: phoneNumber } : { pin }
      );
      isNotFirstTime
        ? logAnalyticEvent(EVENT_NAME.LOGIN, {})
        : logAnalyticEvent(EVENT_NAME.NEW_DEVICE_LOGIN, {});
      const data = response;
      handleNavigation(data);
    } catch (error) {
      setIsPinValid({
        isInvalid: true,
        passwordPolicy: {
          message: {
            ar: t("pinCode.error_message"),
            en: t("pinCode.error_message"),
          },
        },
      });
      console.error("Error in handleLogin api:", error);
    }
  };

  const handleChangePin = async () => {
    try {
      const response = await api.changePasswordSetPin({ pin });
      const data = response;
      handleNavigation(data);
      logAnalyticEvent(EVENT_NAME.CHANGE_PIN, {});
    } catch (error) {
      setIsPinValid({
        isInvalid: true,
        passwordPolicy: {
          message: {
            ar: t("pinCode.error_message"),
            en: t("pinCode.error_message"),
          },
        },
      });
      console.error("Error in handleChangePin api:", error);
    }
  };

  const handleForgotPin = async () => {
    try {
      const response = await api.forgotPasswordSetPin({ pin });
      const data = response;
      logAnalyticEvent(EVENT_NAME.FORGOT_PIN, {});
      handleNavigation(data);
    } catch (error) {
      setIsPinValid({
        isInvalid: true,
        passwordPolicy: {
          message: {
            ar: t("pinCode.error_message"),
            en: t("pinCode.error_message"),
          },
        },
      });
      console.error("Error in handleForgotPin api:", error);
    }
  };

  const handleRegisterSetPin = async () => {
    try {
      const response = await api.registerSetPin({ pin });
      const data = response;
      if (
        data.auth_strategy_status === "FULL" &&
        data.next_auth_factor == null
      ) {
        logAnalyticEvent(EVENT_NAME.REGISTER, {});
        navigate(`/${ROUTES.COMPANY_DETAILS}`);
      }
    } catch (error) {
      setIsPinValid({
        isInvalid: true,
        passwordPolicy: {
          message: {
            ar: t("pinCode.error_message"),
            en: t("pinCode.error_message"),
          },
        },
      });
      console.error("Error in handleRegisterSetPin api:", error);
    }
  };

  const validateRegexArray = (passwordPin) => {
    const result =
      tenantConfig?.customer_password_policy.content.password_complexity_requirements.find(
        (policy, index) => {
          if (!new RegExp(policy.regex).test(passwordPin)) {
            return { isInvalid: true, passwordPolicyIndex: index };
          }
        }
      );
    return result
      ? { isInvalid: true, passwordPolicy: result }
      : { isInvalid: false };
  };

  const handleValidation = () => {
    if (
      pin &&
      (pinState === ACCOUNT_STATE.CREATE ||
        pinState === ACCOUNT_STATE.LOGIN ||
        pinState === ACCOUNT_STATE.FORGOT ||
        pinState === ACCOUNT_STATE.CHANGE_PASSWORD)
    ) {
      const validationResult = validateRegexArray(pin);
      // Login Flow
      if (pin.length > 3 && !validationResult.isInvalid) {
        if (
          pinState === ACCOUNT_STATE.CREATE ||
          pinState === ACCOUNT_STATE.FORGOT ||
          pinState === ACCOUNT_STATE.CHANGE_PASSWORD
        ) {
          setPinState(ACCOUNT_STATE.CONFIRM);
        } else {
          handleLogin();
        }
      } else {
        setIsPinValid(validationResult);
      }
    } // Register and Forgot Flow
    else if (confirmPin && pinState === ACCOUNT_STATE.CONFIRM) {
      const validationResult = validateRegexArray(confirmPin);
      if (confirmPin.length > 3 && !validationResult.isInvalid) {
        if (pin === confirmPin) {
          if (accountState === ACCOUNT_STATE.FORGOT) {
            handleForgotPin();
          } else if (accountState === ACCOUNT_STATE.CHANGE_PASSWORD) {
            handleChangePin();
          } else {
            handleRegisterSetPin();
          }
        } else {
          setIsPinValid({
            isInvalid: true,
            passwordPolicy: {
              message: {
                ar: t("pinCode.enter_same_pin"),
                en: t("pinCode.enter_same_pin"),
              },
            },
          });
        }
      } else {
        setIsPinValid(validationResult);
      }
    }
  };

  useEffect(() => {
    setPinState(accountState);
  }, [location.state]);

  useEffect(() => {
    handleValidation();
  }, [pin, confirmPin]);

  useEffect(() => {
    setStartTime(Date.now());
    return () => {
      const endTime = Date.now();
      logAnalyticEvent(EVENT_NAME.PAGE_VIEW, {
        page_title: ROUTES.VERIFY_PIN,
        path: `${window.location.origin}/${ROUTES.VERIFY_PIN}`,
        time_spent: convertTimestamp(endTime - startTime),
      });
    };
  }, []);

  const renderTitle = useMemo(() => {
    switch (pinState) {
      case ACCOUNT_STATE.CREATE:
        return t("pinCode.create_heading");
      case ACCOUNT_STATE.CONFIRM:
        return t("pinCode.confirm_heading");
      case ACCOUNT_STATE.LOGIN:
        return t("pinCode.welcome_back");
      case ACCOUNT_STATE.FORGOT:
        return t("pinCode.create_heading");
      default:
        return t("pinCode.create_heading");
    }
  }, [pinState]);

  const renderSubtitle = useMemo(() => {
    switch (pinState) {
      case ACCOUNT_STATE.CREATE:
        return t("pinCode.create_description", {
          tenant: tenantConfig?.name,
          pinCount: tenantConfig?.customer_password_policy?.content?.max_length,
        });
      case ACCOUNT_STATE.CONFIRM:
        return t("pinCode.confirm_description");
      case ACCOUNT_STATE.LOGIN:
        return t("pinCode.welcome_back_description");
      case ACCOUNT_STATE.FORGOT:
        return t("pinCode.create_description", {
          tenant: tenantConfig?.name,
          pinCount: tenantConfig?.customer_password_policy?.content?.max_length,
        });
      default:
        return t("pinCode.create_description", {
          tenant: tenantConfig?.name,
          pinCount: tenantConfig?.customer_password_policy?.content?.max_length,
        });
    }
  }, [pinState]);

  const handleBack = async () => {
    try {
      const response = await api.authBackSession();
      navigate(-1);
    } catch (error) {
      console.error("Error in handleBack api:", error);
    }
  };
  // authBackSession
  return (
    <div className="verify-pin-container">
      <Header backEnable onClick={() => handleBack()} />
      <div className="content-container">
        <h1 className="title">{renderTitle}</h1>
        <p className="description">{renderSubtitle}</p>
      </div>
      <div className="code-container">
        <PINCode
          pinLength={
            tenantConfig?.customer_password_policy?.content?.max_length
          }
          pin={pinState === ACCOUNT_STATE.CONFIRM ? confirmPin : pin}
          hasError={isPinValid.isInvalid}
          errorMessage={
            isPinValid.passwordPolicy?.message[selectedLanguage?.value]
          }
        />
      </div>
      {pinState === "create" ? (
        <div className="info-text-container">
          <img src={AlertCircle} alt="info" className="info-icon" />
          <span className={isRTL ? "info-text-rtl" : "info-text"}>
            {t("pinCode.create_infotext", {
              pinCount:
                tenantConfig?.customer_password_policy?.content?.max_length,
            })}
          </span>
        </div>
      ) : null}
      <div className="footer-container">
        <DialPad
          onNumberPress={handleNumberPress}
          onDeletePress={handleDelete}
        />
        {pinState === "login" ? (
          <p
            className="forgot-text"
            onClick={() =>
              navigate(`/${ROUTES.VERIFY_OTP}`, {
                state: {
                  isForgotPassword: true,
                  isNewCustomer: false,
                  phoneNumber: phoneNumber,
                },
              })
            }
          >
            {t("pinCode.footer_text")}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default VerifyPIN;
